/**
 * State mutation handlers (s:*).
 */

import type { FlowStep } from "../types/flow";
import type { ExecutionContext } from "../context/execution-context";
import { parsePath } from "../expressions/path";
import { resolveValue } from "../expressions/resolve";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function parseTarget(
 step: FlowStep,
 ctx: ExecutionContext,
): { scope: string; segments: readonly string[] } | null {
 const target = asRecord(step)["@"];
 if (typeof target !== "string") return null;

 const resolvedTarget =
 target.startsWith("@") && (target.match(/\./g)?.length ??0) >0
 ? target
 : (resolveValue(target, ctx) as string);

 const final =
 typeof resolvedTarget === "string" && resolvedTarget.startsWith("@")
 ? resolvedTarget
 : target;

 return parsePath(final);
}

function parseUpdateTarget(
 step: FlowStep,
 ctx: ExecutionContext,
): { scope: string; segments: readonly string[] } | null {
 const rawTarget = asRecord(step)["path"] ?? asRecord(step)["@"];
 if (typeof rawTarget !== "string") return null;

 const resolvedTarget = resolveValue(rawTarget, ctx);
 const target =
 typeof resolvedTarget === "string" && resolvedTarget.trim()
 ? resolvedTarget.trim()
 : rawTarget.trim();

 const pathExpression = target.startsWith("@") ? target : `@state.${target}`;
 return parsePath(pathExpression);
}

function readPathValue(
 root: unknown,
 segments: readonly string[],
): unknown {
 let cur: unknown = root;

 for (const segment of segments) {
 if (cur === null || cur === undefined || typeof cur !== "object") {
 return undefined;
 }
 cur = (cur as Record<string, unknown>)[segment];
 }

 return cur;
}

function readTargetValue(
 ctx: ExecutionContext,
 target: { scope: string; segments: readonly string[] },
): unknown {
 const root = ctx.getScope(target.scope);

 if (target.scope === "state") {
 if (target.segments.length ===0) return root;
 const [slotName, ...rest] = target.segments;
 if (!slotName) return undefined;

 const slotValue =
 root && typeof root === "object" && !Array.isArray(root)
 ? (root as Record<string, unknown>)[slotName]
 : undefined;

 return rest.length ===0 ? slotValue : readPathValue(slotValue, rest);
 }

 if (target.segments.length ===0) return root;
 return readPathValue(root, target.segments);
}

registerStepHandler("s:set", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);
 ctx.writeState(target.scope, target.segments, value);
});

/**
 * Compatibility form used by flow documents that express state writes as:
 *
 * - type: state:update
 * path: quickStartOpen
 * value: true
 *
 * Bare paths target the root state scope. Explicit @ paths are also accepted.
 */
registerStepHandler("state:update", (step, ctx) => {
 const target = parseUpdateTarget(step, ctx);
 if (!target) return;

 const record = asRecord(step);
 const rawValue = Object.prototype.hasOwnProperty.call(record, "value")
 ? record["value"]
 : record["v"];
 const value = resolveValue(rawValue, ctx);
 ctx.writeState(target.scope, target.segments, value);
});

registerStepHandler("s:merge", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);
 if (!value || typeof value !== "object") return;

 const existing = readTargetValue(ctx, target);
 const merged =
 existing && typeof existing === "object" && !Array.isArray(existing)
 ? { ...existing, ...(value as Record<string, unknown>) }
 : { ...(value as Record<string, unknown>) };

 ctx.writeState(target.scope, target.segments, merged);
});

registerStepHandler("s:append", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);

 const existing = readTargetValue(ctx, target);
 const arr = Array.isArray(existing) ? [...existing, value] : [value];
 ctx.writeState(target.scope, target.segments, arr);
});

registerStepHandler("s:push", (step, ctx) => {
 // Alias of append
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);

 const existing = readTargetValue(ctx, target);
 const arr = Array.isArray(existing) ? [...existing, value] : [value];
 ctx.writeState(target.scope, target.segments, arr);
});

registerStepHandler("s:concat", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);

 const existing = readTargetValue(ctx, target);

 if (Array.isArray(existing) && Array.isArray(value)) {
 ctx.writeState(target.scope, target.segments, [...existing, ...value]);
 } else if (typeof existing === "string") {
 const v = Array.isArray(value) ? value.join("") : String(value ?? "");
 ctx.writeState(target.scope, target.segments, existing + v);
 } else {
 const v = Array.isArray(value) ? value : [value];
 ctx.writeState(target.scope, target.segments, v);
 }
});

registerStepHandler("s:del", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 ctx.deleteState(target.scope, target.segments);
});

registerStepHandler("form:set", (step, ctx) => {
 const r = asRecord(step);
 const field = r["field"];
 if (typeof field !== "string") return;
 const value = resolveValue(r["v"], ctx);
 ctx.setForm(field, value);
});

registerStepHandler("pref:set", (step, ctx) => {
 const target = parseTarget(step, ctx);
 if (!target) return;
 const value = resolveValue(asRecord(step)["v"], ctx);
 ctx.writeState("pref", target.segments, value);
});